import { UserStat } from "types/user.type";

export interface UserSegment {
  /** URL slug, under /apps-users-list/. */
  key: string;
  /** Card caption and page heading. Kept short — these labels sit in a
   *  five-across grid on desktop and a two-across one on a phone, and a label
   *  that wraps to three lines makes every card in the row that tall. */
  label: string;
  role?: string;
  kycStatus?: string;
  count: (stats?: UserStat) => number;
}

/**
 * The slices of the user base an operator works in.
 *
 * Each is a page of its own rather than a filter applied in place. Filtering in
 * place meant the URL never changed, so a segment could not be linked to,
 * bookmarked, reloaded or reached by the back button — and the table silently
 * became something other than what its heading said.
 *
 * `role` and `kycStatus` are passed to the API as exact filters, which is what
 * `countUserByRole` uses for the numbers on the cards. The two therefore
 * reconcile; under the old `search=Mynda` regex they did not have to.
 */
export const USER_SEGMENTS: UserSegment[] = [
  {
    key: "all",
    label: "All users",
    count: (stats) => stats?.totalUsers ?? 0,
  },
  {
    key: "myndas",
    label: "Myndas",
    role: "Mynda",
    count: (stats) => stats?.totalMyndas ?? 0,
  },
  {
    key: "employers",
    label: "Employers",
    role: "Employer",
    count: (stats) => stats?.totalEmployers ?? 0,
  },
  {
    key: "myndas-in-review",
    label: "Myndas in review",
    role: "Mynda",
    kycStatus: "In Review",
    count: (stats) => stats?.reviewMyndas ?? 0,
  },
  {
    key: "employers-in-review",
    label: "Employers in review",
    role: "Employer",
    kycStatus: "In Review",
    count: (stats) => stats?.reviewEmployers ?? 0,
  },
  {
    key: "myndas-approved",
    label: "Myndas approved",
    role: "Mynda",
    kycStatus: "Approved",
    count: (stats) => stats?.approvedMyndas ?? 0,
  },
  {
    key: "employers-approved",
    label: "Employers approved",
    role: "Employer",
    kycStatus: "Approved",
    count: (stats) => stats?.approvedEmployers ?? 0,
  },
  {
    key: "myndas-rejected",
    label: "Myndas rejected",
    role: "Mynda",
    kycStatus: "Rejected",
    count: (stats) => stats?.rejectedMyndas ?? 0,
  },
  {
    key: "employers-rejected",
    label: "Employers rejected",
    role: "Employer",
    kycStatus: "Rejected",
    count: (stats) => stats?.rejectedEmployers ?? 0,
  },
];

export const segmentFor = (key?: string): UserSegment | undefined =>
  USER_SEGMENTS.find((segment) => segment.key === key);

export const segmentPath = (key: string) => `/apps-users-list/${key}`;
